<template>
  <!--
    A hub's own pages with no credential behind them (card `t_bfe0edc9`).

    The measured sequence this replaces: the route fell to `/home` while the hub
    tab went on painting the hub's shell — navigation, dashboards, a look that
    promises data, and every request unanswered. `App.vue` asks
    `mayRenderHubShell()` before it chooses a layout, and this is what it paints
    instead: the hub named, the fact stated, and the two ways on (the Local page,
    which is where this device's ways into a hub live, and the launcher).
  -->
  <div class="flex min-h-screen items-center justify-center bg-gray-100 px-6 dark:bg-gray-900">
    <div class="flex w-full max-w-md flex-col items-center gap-4 text-center">
      <span class="material-symbols-outlined text-4xl text-amber-500">key_off</span>
      <h1 class="text-base font-semibold text-gray-900 dark:text-gray-100" data-needs-credential-title>
        {{ t('hub.open.title', { hub: hubName }) }}
      </h1>
      <p class="text-sm text-gray-600 dark:text-gray-300" data-needs-credential-reason>
        {{ t(messageKey, { hub: hubName, address: address ?? '—' }) }}
      </p>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        {{ t('hub.open.needsCredential') }}
      </p>
      <div class="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          class="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          data-needs-credential-local
          @click="openLocalPage">
          {{ t('hub.open.openLocalPage') }}
        </button>
        <button
          type="button"
          class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          @click="openServers">
          {{ t('hub.open.openServers') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * The needs-join state for a hub page (card `t_bfe0edc9`).
 *
 * Self-contained on purpose: it reads the window's own hub record
 * (`readTabActiveHub` — the per-tab marker, not the shared keys) and hands the
 * failure to the Local page exactly the way `utils/hubOpenFallback.ts` writes it,
 * so the page paints the same sentence this surface does.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { readTabActiveHub } from '../../config/runtime';
import { formatHubOpenAddress, hubOpenMessageKey } from '../../utils/hubOpenFallback';

const { t } = useI18n();
const router = useRouter();

/** The hub this window is on — its own record, so one tab cannot speak for another. */
const hub = readTabActiveHub();

const hubName = computed(() => hub?.name?.trim() || hub?.hubId?.trim() || t('enroll.hub.unknown'));
const address = computed(() => formatHubOpenAddress({ ip: hub?.ip ?? '', port: hub?.port }));
const messageKey = hubOpenMessageKey('no-credential');

/**
 * The Local page, carrying the hub and the reason — the same query the connect
 * failures write, so the page's banner and this screen say one thing.
 */
function openLocalPage(): void {
  const query: Record<string, string> = { reason: 'no-credential' };
  if (hub?.name?.trim()) query.hub = hub.name.trim();
  if (address.value) query.address = address.value;
  void router.replace({ path: '/local', query });
}

function openServers(): void {
  void router.replace('/home');
}
</script>
